import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface UserPreferences {
  id: string;
  user_id: string;
  migration_banner_dismissed: boolean;
  migration_banner_dismissed_at: string | null;
  passcode_nudge_resolved: boolean;
  onboarding_hints_dismissed: Record<string, boolean>;
  onboarding_checklist_dismissed: boolean;
  onboarding_checklist_minimised: boolean;
  kiosk_promo_dismissed: boolean;
}


export function useUserPreferences() {
  const { user } = useAuth();

  return useQuery<UserPreferences | null>({
    queryKey: ["user_preferences", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("user_preferences" as any)
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as UserPreferences | null;
    },
    enabled: !!user?.id,
    staleTime: 30_000,
  });
}

export function useUpsertPreference() {
  const { user } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (updates: Partial<Omit<UserPreferences, "id" | "user_id">>) => {
      if (!user?.id) throw new Error("Not authenticated");

      // Try update first
      const { data: existing } = await supabase
        .from("user_preferences" as any)
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from("user_preferences" as any)
          .update(updates)
          .eq("user_id", user.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("user_preferences" as any)
          .insert({ user_id: user.id, ...updates });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["user_preferences"] });
    },
  });
}

export function useDismissMigrationBanner() {
  const upsert = useUpsertPreference();
  return () =>
    upsert.mutateAsync({
      migration_banner_dismissed: true,
      migration_banner_dismissed_at: new Date().toISOString(),
    });
}

export function useDismissOnboardingHint() {
  const { data: prefs } = useUserPreferences();
  const upsert = useUpsertPreference();

  return (pathname: string) => {
    const current = (prefs?.onboarding_hints_dismissed || {}) as Record<string, boolean>;
    upsert.mutate({
      onboarding_hints_dismissed: { ...current, [pathname]: true },
    });
  };
}
